/** Durable FIFO for append-only events. Unlike Golf's cell queue, rallies must never coalesce. */
const MAX_EVENTS = 500
const equal = (a, b) => JSON.stringify(canonical(a)) === JSON.stringify(canonical(b))
const canonical = value => Array.isArray(value) ? value.map(canonical) : value && typeof value === 'object'
  ? Object.fromEntries(Object.keys(value).sort().map(k => [k, canonical(value[k])])) : value
const failure = code => Object.assign(new Error(code), { code })
export function racquetQueueKey({ clubCode, memberId, matchId }) {
  if (![clubCode, memberId, matchId].every(v => typeof v === 'string' && v.length > 0)) throw failure('queue_identity_required')
  return `privana:racquet-events:v1:${[clubCode, memberId, matchId].map(encodeURIComponent).join(':')}`
}
export function createRacquetEventQueue(scope, { storage = globalThis.localStorage, onChange = () => {} } = {}) {
  const key = racquetQueueKey(scope)
  let events = [], blocked = null, storageError = null, running = null, lastFailure = null
  const snapshot = () => ({ events: structuredClone(events), blocked, storageError, lastFailure, saved: events.length === 0 && !blocked && !storageError })
  const emit = () => onChange(snapshot())
  const persist = () => {
    try { storage.setItem(key, JSON.stringify({ version: 1, events })); storageError = null; return true }
    catch { storageError = 'queue_storage_failed'; return false }
  }
  try {
    const raw = storage.getItem(key)
    if (raw !== null) {
      const parsed = JSON.parse(raw)
      if (parsed?.version !== 1 || !Array.isArray(parsed.events) || parsed.events.length > MAX_EVENTS || parsed.events.some(e => !e?.id || !Number.isInteger(e.expectedRevision) || e.expectedRevision < 0 || !e.operation?.type) || new Set(parsed.events.map(e => e.id)).size !== parsed.events.length) throw failure('queue_corrupt')
      events = parsed.events
    }
  } catch { blocked = 'queue_unreadable' }
  return {
    snapshot,
    enqueue(event) {
      if (blocked) throw failure(blocked)
      if (!event?.id || !Number.isInteger(event.expectedRevision) || event.expectedRevision < 0 || !event.operation?.type) throw failure('invalid_queue_event')
      const previous = events.find(e => e.id === event.id)
      if (previous) { if (!equal(previous, event)) throw failure('idempotency_conflict'); return snapshot() }
      if (events.length >= MAX_EVENTS) throw failure('queue_capacity_reached')
      const last = events.at(-1)
      if (last && event.expectedRevision !== last.expectedRevision + 1) throw failure('queue_revision_gap')
      events.push(structuredClone(event))
      // Keep the visible event in memory on quota/security failure, and expose the
      // failure. The caller must not claim restart safety or server acknowledgement.
      persist(); emit(); return snapshot()
    },
    retryStorage() { if (blocked === 'queue_unreadable') throw failure('queue_unreadable'); persist(); emit(); return snapshot() },
    flush(send, { batchSize = 1, onAccepted = () => {} } = {}) {
      if (!Number.isInteger(batchSize) || batchSize < 1 || batchSize > 32) throw failure('invalid_batch_size')
      if (running) return running
      running = (async () => {
        while (events.length && !blocked && !storageError) {
          const batch = structuredClone(events.slice(0, batchSize))
          const event = batch[0]
          let response
          try { response = await send(batchSize === 1 ? event : batch) }
          catch (error) {
            lastFailure = { eventId: event.id, expectedRevision: event.expectedRevision, code: error.code || error.name || 'request_failed', status: error.status || error.statusCode || null }
            if (error.status === 409 || error.statusCode === 409) blocked = 'revision_conflict'
            else if ([400, 401, 403, 404, 422].includes(error.status || error.statusCode)) blocked = 'event_rejected'
            // Offline/provider errors preserve FIFO; a later reconnect may retry.
            emit(); return snapshot()
          }
          const matches = (e, event) => e?.id === event.id && e.revision === event.expectedRevision + 1 && equal(e.operation, event.operation) && e.provenance === event.provenance
          const verified = Array.isArray(response?.events) && (batchSize === 1
            ? batch.every(event => response.events.some(e => matches(e, event)))
            : response.events.length === batch.length && batch.every((event, i) => matches(response.events[i], event)))
          if (!verified) {
            lastFailure = {eventId:event.id,expectedRevision:event.expectedRevision,code:'acknowledgement_invalid'}; blocked = 'acknowledgement_invalid'; emit(); return snapshot()
          }
          // Validate every acknowledgement before updating the projection or removing any work.
          try { onAccepted(response, batch) }
          catch { lastFailure = { eventId: event.id, expectedRevision: event.expectedRevision, code: 'acknowledgement_invalid' }; blocked = 'acknowledgement_invalid'; emit(); return snapshot() }
          events.splice(0, batch.length); lastFailure = null; persist(); emit()
          if (storageError) break
        }
        return snapshot()
      })().finally(() => { running = null })
      return running
    },
    // Conflict resolution deliberately does not rebase old rallies onto a newer
    // score. Export lets a review UI retain all local work before resolving it.
    discardAfterReview() {
      if (running) throw failure('queue_busy')
      try { storage.setItem(key, JSON.stringify({ version: 1, events: [] })) } catch { storageError = 'queue_storage_failed'; emit(); throw failure('queue_storage_failed') }
      events = []; blocked = null; storageError = null; lastFailure = null; emit(); return snapshot()
    },
    exportPending() { return JSON.stringify({ scope, ...snapshot() }, null, 2) },
  }
}
